/**
 * PageHeader — Lembra authed-surface page header.
 *
 * MS16 upgrade: now supports the Heidi editorial system used across
 * the public landing surfaces (eyebrow + Newsreader italic-emphasis
 * H1 + DM Sans lede). Existing callers stay backward compatible —
 * passing only `title` + `subtitle` renders the original simple
 * navy/bold/lede shape so a 15-page rollout doesn't have to happen
 * in one PR.
 *
 * New props:
 *   - eyebrow:  small mono-uppercase tag above the title (coral)
 *   - emphasis: a substring of the title rendered as italic + coral.
 *               If the word appears verbatim in the title, only that
 *               word is emphasised; otherwise the emphasis is
 *               appended in italic-coral after the title text.
 *   - tail:     inline ReactNode rendered on the right (e.g. an
 *               "Add team member" button). Stacks below on mobile.
 *
 * Pioneer alignment:
 *  - Pablo Stanley: real <h1> for screen readers; emphasis is just
 *    a styled <em> that doesn't change semantic level.
 *  - Norman: typographic hierarchy reads instantly; the eyebrow
 *    signifies "you're in <surface>" without redundant breadcrumb.
 */

import type { ReactNode } from "react";

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Mono-uppercase tag above the title (coral). Heidi system. */
  eyebrow?: string;
  /** Word in the title to render as italic + coral. Heidi system. */
  emphasis?: string;
  /** Inline element rendered to the right (button, link, etc). */
  tail?: ReactNode;
}

const displayFamily = "Newsreader, Georgia, serif";
const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sansFamily = "DM Sans, sans-serif";

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  emphasis,
  tail,
}: PageHeaderProps) {
  // If no eyebrow / emphasis / tail provided, render the original
  // simple shape so back-compat is bit-perfect for existing pages
  // that haven't migrated to the Heidi system yet.
  const isHeidi = Boolean(eyebrow ?? emphasis ?? tail);

  if (!isHeidi) {
    return (
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-navy">{title}</h1>
        {subtitle && (
          <p className="mt-1 text-charcoal/70 text-lg">{subtitle}</p>
        )}
      </div>
    );
  }

  // Split the title around the emphasis word so we can wrap that
  // single word in <em>. If `emphasis` doesn't appear in `title`,
  // we render it as a trailing italic emphasis instead.
  const renderTitle = () => {
    if (!emphasis) {
      return title;
    }
    const idx = title.toLowerCase().indexOf(emphasis.toLowerCase());
    if (idx === -1) {
      return (
        <>
          {title}{" "}
          <em
            style={{
              fontStyle: "italic",
              fontWeight: 500,
              color: "#C4564A",
            }}
          >
            {emphasis}
          </em>
        </>
      );
    }
    const before = title.slice(0, idx);
    const word = title.slice(idx, idx + emphasis.length);
    const after = title.slice(idx + emphasis.length);
    return (
      <>
        {before}
        <em
          style={{
            fontStyle: "italic",
            fontWeight: 500,
            color: "#C4564A",
          }}
        >
          {word}
        </em>
        {after}
      </>
    );
  };

  return (
    <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
      <div>
        {eyebrow && (
          <div
            style={{
              fontFamily: monoFamily,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(196,86,74,0.85)",
              marginBottom: 10,
            }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          style={{
            fontFamily: displayFamily,
            fontWeight: 500,
            fontSize: "clamp(1.85rem, 3.4vw, 2.6rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.018em",
            color: "#1D3461",
            margin: 0,
          }}
        >
          {renderTitle()}
        </h1>
        {subtitle && (
          <p
            style={{
              fontFamily: sansFamily,
              fontSize: 16,
              lineHeight: 1.55,
              color: "rgba(45,41,38,0.7)",
              marginTop: 8,
              marginBottom: 0,
              maxWidth: "60ch",
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {tail && <div className="shrink-0">{tail}</div>}
    </div>
  );
}
